import { getThresholdChipKey } from '@/ui/composites/table/tableUtils';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { formatFrequency } from '@/ui/formatters/formatFrequency';
import { formatPercent } from '@/ui/formatters/formatPercent';
import type { MetricsDto } from '@/features/metrics/metricsHelpers';
import type { StatusTone } from '@/ui/primitives/Chip';
import type { MetricThresholdGroup } from '@flowqa/shared';

export type MetricDeltaTone = 'positive' | 'negative' | 'neutral';

export type MetricValueItem = {
  id: string;
  label: string;
  valueLabel: string;
  valueTone: StatusTone;
  deltaLabel: string;
  deltaTone: MetricDeltaTone;
};

const resolveDeltaTone = (
  delta: number | null,
  direction: MetricThresholdGroup[keyof MetricThresholdGroup]['direction'],
): MetricDeltaTone => {
  if (delta === null || delta === 0) return 'neutral';
  if (direction === 'higherBetter') return delta > 0 ? 'positive' : 'negative';
  return delta < 0 ? 'positive' : 'negative';
};

const formatDelta = (
  value: number | null,
  formatter: (value: number) => string,
  showPlaceholder: boolean,
): string => {
  if (value === null || value === 0) return showPlaceholder ? '-' : '';
  const sign = value > 0 ? '+' : '-';
  return `${sign}${formatter(Math.abs(value))}`;
};

const resolveValue = (value: number | null, formatter: (value: number) => string): string =>
  value === null ? '-' : formatter(value);

const resolvePassRate = (metrics: MetricsDto | null): number | null => {
  if (!metrics) return null;
  const passed = metrics.statusMetrics.find((metric) => metric.key === 'passed');
  return passed?.percentage ?? null;
};

export const buildMetricValueItems = ({
  metrics,
  previousMetrics,
  thresholds,
  frequencyOverride,
  previousFrequencyOverride,
  passRateOverride,
  previousPassRateOverride,
  showDelta,
}: {
  metrics: MetricsDto | null;
  previousMetrics: MetricsDto | null;
  thresholds: MetricThresholdGroup;
  frequencyOverride?: number | null;
  previousFrequencyOverride?: number | null;
  passRateOverride?: number | null;
  previousPassRateOverride?: number | null;
  showDelta?: boolean;
}): MetricValueItem[] => {
  const includeDeltas = showDelta ?? Boolean(previousMetrics);
  const passRateValue = passRateOverride ?? resolvePassRate(metrics);
  const passRateDelta =
    passRateValue !== null
      ? passRateValue -
        (previousPassRateOverride ?? resolvePassRate(previousMetrics) ?? passRateValue)
      : null;
  const passRateLabel = resolveValue(passRateValue, (value) => `${formatPercent(value)}%`);
  const passRateDeltaLabel = includeDeltas
    ? formatDelta(passRateDelta, (value) => `${formatPercent(value)}%`, includeDeltas)
    : '';
  const passRateTone = getThresholdChipKey(passRateValue, thresholds.passRate) ?? 'transparent';

  const frequencyValue = frequencyOverride ?? metrics?.averages.frequencyPerDay ?? null;
  const frequencyDelta =
    frequencyValue !== null
      ? frequencyValue -
        (previousFrequencyOverride ?? previousMetrics?.averages.frequencyPerDay ?? frequencyValue)
      : null;
  const frequencyLabel = resolveValue(frequencyValue, formatFrequency);
  const frequencyDeltaLabel = includeDeltas
    ? formatDelta(frequencyDelta, formatFrequency, includeDeltas)
    : '';
  const frequencyTone = getThresholdChipKey(frequencyValue, thresholds.frequency) ?? 'transparent';

  const durationSeconds = metrics?.averages.avgDurationSeconds ?? null;
  const durationDelta =
    durationSeconds !== null
      ? durationSeconds - (previousMetrics?.averages.avgDurationSeconds ?? durationSeconds)
      : null;
  const durationLabel = resolveValue(durationSeconds, (value) => formatDuration(value * 1000));
  const durationDeltaLabel = includeDeltas
    ? formatDelta(durationDelta, (value) => formatDuration(value * 1000), includeDeltas)
    : '';
  const durationTone =
    getThresholdChipKey(durationSeconds, thresholds.avgDuration) ?? 'transparent';

  return [
    {
      id: 'passRate',
      label: 'Pass Rate',
      valueLabel: passRateLabel,
      valueTone: passRateTone,
      deltaLabel: passRateDeltaLabel,
      deltaTone: resolveDeltaTone(passRateDelta, thresholds.passRate.direction),
    },
    {
      id: 'frequency',
      label: 'Frequency',
      valueLabel: frequencyLabel,
      valueTone: frequencyTone,
      deltaLabel: frequencyDeltaLabel,
      deltaTone: resolveDeltaTone(frequencyDelta, thresholds.frequency.direction),
    },
    {
      id: 'avgDuration',
      label: 'Avg. Duration',
      valueLabel: durationLabel,
      valueTone: durationTone,
      deltaLabel: durationDeltaLabel,
      deltaTone: resolveDeltaTone(durationDelta, thresholds.avgDuration.direction),
    },
  ];
};
